/*
 *  -----------------------------------------------------------------------
 *    <copyright file="error-boundary.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { datadogLogs } from '@datadog/browser-logs';
import Drawer from '@mui/material/Drawer';
import cn from 'clsx';
import { BrowserHistory } from 'history';
import { makeStyles } from 'tss-react/mui';
import LogoImage from '../../public/img/logo.png';
import OpraIcon from '../../public/img/opra.svg?react';
import Log from '../utils/logger';
import styles from './style.module.scss';
import { InfoField } from '@components-clean/core/info-field';
import { PageNavErrorSignout } from '@components-clean/page-nav/page-nav-footer/page-nav-error-signout';
import Constants from '@utils/constants';

interface IState {
    hasError: boolean;
    error: any;
    info: any;
    eventId: any;
}

interface ErrorBoundaryProps extends WithTranslation {
    history: BrowserHistory;
    children: any;
    muiStyles: Record<'drawer' | 'drawerExpanded', string>;
    layoutError: boolean;
}

// Taken from: https://www.loggly.com/blog/best-practices-for-client-side-logging-and-error-handling-in-react/
class ErrorBoundary extends Component<ErrorBoundaryProps, IState> {
    unlisten: any = null;

    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            info: null,
            eventId: null,
        };

        const { history } = this.props;

        if (history.listen) {
            this.unlisten = history.listen(() => {
                if (this.state.hasError) {
                    this.setState({
                        hasError: false,
                    });
                }
            });
        }
    }

    static getDerivedStateFromError(error: any) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        // log the error to our server with loglevel
        Log.error({ error, info });

        this.setState((prevState) => ({ ...prevState, error, info }));
    }

    componentWillUnmount() {
        if (this.unlisten) this.unlisten();
    }

    render() {
        const { hasError, error } = this.state;
        const { children, muiStyles, layoutError, t } = this.props;
        const menuIconSVG =
            // eslint-disable-next-line max-len
            'M0 88C0 74.7 10.7 64 24 64l400 0c13.3 0 24 10.7 24 24s-10.7 24-24 24L24 112C10.7 112 0 101.3 0 88zM0 248c0-13.3 10.7-24 24-24l400 0c13.3 0 24 10.7 24 24s-10.7 24-24 24L24 272c-13.3 0-24-10.7-24-24zM448 408c0 13.3-10.7 24-24 24L24 432c-13.3 0-24-10.7-24-24s10.7-24 24-24l400 0c13.3 0 24 10.7 24 24z';

        const { environment } = window;
        if (environment !== Constants.environment.LOCAL) {
            if (!datadogLogs.getInternalContext()) {
                datadogLogs.init({
                    clientToken: 'pub7179cad0b2464215f8a5a899c42650fa',
                    site: 'datadoghq.eu',
                    service: 'OPRA 5',
                    env: environment as string | undefined,
                    version: import.meta.env.VITE_VERSION,
                    sessionSampleRate: 100,
                });
            }
        }

        if (hasError) {
            const id = crypto.randomUUID();
            datadogLogs.logger.error(`${error.message}`, { id }, error);

            // You can render any custom fallback UI
            return (
                <>
                    {layoutError && (
                        <Drawer
                            variant="permanent"
                            className={cn(muiStyles.drawer, muiStyles.drawerExpanded)}
                            classes={{
                                paper: cn(
                                    'k-h-screen',
                                    styles.drawerPaper,
                                    styles.pageNavDrawerFont,
                                    muiStyles.drawerExpanded,
                                ),
                            }}
                        >
                            <div className={`k-overflow-hidden ${styles.drawerContent}`}>
                                <div
                                    className={cn('k-w-full k-d-flex k-align-items-center', {
                                        [styles.drawerHeader]: true,
                                        'k-justify-content-between k-px-4': true,
                                    })}
                                >
                                    <div className={styles.drawerHeaderLightgray}>
                                        <div className="k-d-flex k-align-items-center k-gap-2.5 k-mb-auto">
                                            <img alt="logo" className={styles.drawerHeaderLogo} src={LogoImage} />
                                            <OpraIcon role="img" title="logo-label" className="k-mb-2.5" />
                                        </div>
                                    </div>
                                    <div className="text-box k-flex-grow">
                                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                        <label className="k-label k-d-flex k-align-items-end" />
                                        <span
                                            className={cn(
                                                'k-textbox k-input k-input-md k-input-solid k-rounded-md',
                                                styles.searchTextbox,
                                            )}
                                        >
                                            {/* eslint-disable-next-line max-len */}
                                            <span className="k-input-prefix k-input-prefix-horizontal k-display-flex k-align-items-center k-color-white">
                                                <span
                                                    className="k-icon k-svg-icon k-svg-i-search k-icon-lg k-p-3"
                                                    aria-hidden="true"
                                                >
                                                    <svg
                                                        aria-hidden="true"
                                                        focusable="false"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 512 512"
                                                    >
                                                        {/* eslint-disable-next-line max-len */}
                                                        <path d="M365.3 320h-22.7l-26.7-26.7C338.5 265.7 352 230.4 352 192c0-88.4-71.6-160-160-160S32 103.6 32 192s71.6 160 160 160c38.4 0 73.7-13.5 101.3-36.1l26.7 26.7v22.7L434.7 480l45.3-45.3zM64 192c0-70.7 57.3-128 128-128s128 57.3 128 128-57.3 128-128 128S64 262.7 64 192" />
                                                    </svg>
                                                </span>
                                            </span>
                                            <input
                                                className="k-input-inner"
                                                placeholder="Search"
                                                value=""
                                                data-testid="nav-search-textbox"
                                                readOnly
                                            />
                                        </span>
                                    </div>
                                    <div className={cn(styles.drawerHeaderBottomGradient, {})} />
                                    <div className={styles.drawerHeaderLightgrayTriangle} />
                                    <svg
                                        data-prefix="far"
                                        data-icon="bars"
                                        className={cn(
                                            'svg-inline--fa fa-bars k-color-white k-h-5 k-w-5 k-pos-absolute k-right-4 k-z-10',
                                            styles.menuIconPosition,
                                        )}
                                        role="img"
                                        viewBox="0 0 448 512"
                                        aria-hidden="true"
                                        data-testid="expand-sidebar-button"
                                    >
                                        <path d={menuIconSVG} fill="currentColor" />
                                    </svg>
                                </div>
                            </div>

                            <PageNavErrorSignout />
                        </Drawer>
                    )}

                    <div className={cn('k-h-full k-w-full', styles.errorMessage)}>
                        <div
                            className={cn('k-d-grid k-align-content-center k-justify-content-center k-h-full', {
                                [styles.errorMessageNavBarPosition]: layoutError,
                            })}
                        >
                            <InfoField
                                type="Error"
                                message={`${t('common:fe_ErrorMessage_ErrorId')}: ${id}. ${t('common:fe_ErrorMessage_Text')}`}
                            />
                        </div>
                    </div>
                </>
            );
        }

        return children;
    }
}

function withStyles(Wrapper: Component) {
    return function WrappedComponents(props: Omit<ErrorBoundaryProps, 'muiStyles'>) {
        const drawerExpandedWidth = 260;

        const muiStyles = makeStyles()((theme) => ({
            drawer: {
                width: drawerExpandedWidth,
                flexShrink: 0,
                whiteSpace: 'nowrap',
            },
            drawerExpanded: {
                width: drawerExpandedWidth,
                transition: theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            },
        }))();

        // @ts-ignore
        return <Wrapper {...props} muiStyles={muiStyles} />;
    };
}

// @ts-ignore
export default withStyles(withTranslation()(ErrorBoundary));
